// Prompt: editor-facing rationale draft (PRD §7 Feature 5, §13).
// Synthesises the pattern findings + classifier output + chosen decision into a
// short paragraph the editor can edit before sending. Editor remains authoritative.

import type { ChatRequest } from '../types';
import type { PatternRunResult } from '../../patterns';
import type { ManuscriptDocument } from '../../pdf/types';
import { DECISION_LABELS, type DecisionLabel } from '../../export/types';

export const PROMPT_VERSION = 'draft_rationale-1.0';

const SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['rationale'],
  properties: {
    rationale: { type: 'string', minLength: 1, maxLength: 1200 },
  },
} as const;

export interface DraftRationaleOutput {
  rationale: string;
}

export interface DraftRationaleArgs {
  manuscript: ManuscriptDocument;
  pattern: PatternRunResult;
  decision: DecisionLabel;
}

function topMissing(args: DraftRationaleArgs, limit = 5): string {
  const m = args.pattern.methodology;
  if (!m) return '';
  return m.items
    .filter((i) => i.status === 'not_located' && i.severityIfMissing === 'red')
    .slice(0, limit)
    .map((i) => `- ${i.itemId}: ${i.itemText}`)
    .join('\n');
}

function topIntegrity(args: DraftRationaleArgs, limit = 5): string {
  return args.pattern.findings
    .filter((f) => f.category !== 'methodology' && (f.severity === 'red' || f.severity === 'amber'))
    .slice(0, limit)
    .map((f) => `- [${f.severity}] ${f.findingText}`)
    .join('\n');
}

export function buildPrompt(args: DraftRationaleArgs): ChatRequest {
  const systemPrompt = `You are an editorial assistant drafting a board editor's recommendation rationale for the chief editor. Write 3-6 sentences. Be neutral, evidence-based, and specific — name the methodology items or integrity flags driving the recommendation. Do not invent findings. The editor will edit your draft before sending.`;

  const userPrompt = `Manuscript: "${args.manuscript.title || '(untitled)'}"\nDetected study type: ${args.pattern.classifier.label}\nApplicable guideline: ${args.pattern.methodology?.guidelineId ?? 'none'}\n\nProposed decision: ${DECISION_LABELS[args.decision]}\n\nHigh-severity methodology items not located by the pattern engine:\n${topMissing(args) || '(none)'}\n\nIntegrity flags (amber/red):\n${topIntegrity(args) || '(none)'}\n\nReturn JSON: { "rationale": <3-6 sentences> }`;

  return {
    systemPrompt,
    userPrompt,
    schema: SCHEMA,
    maxTokens: 600,
    promptVersion: PROMPT_VERSION,
  };
}

export function parseResponse(json: unknown): DraftRationaleOutput | null {
  if (typeof json !== 'object' || json === null) return null;
  const o = json as Record<string, unknown>;
  if (typeof o.rationale !== 'string' || o.rationale.trim().length < 20) return null;
  return { rationale: o.rationale };
}
